import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import NotificationBell from '../components/NotificationBell';

const API = process.env.REACT_APP_API_URL || '';

const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

const fmt2 = (v) => {
  const n = parseFloat(v || 0);
  return n === 0 ? '—' : n.toFixed(2);
};

const fmtH = (h) => (h ? h.slice(0, 5) : '—');

const dateRef = (offset) => {
  const d = new Date();
  d.setDate(d.getDate() + offset * 7);
  return d.toISOString().slice(0, 10);
};

const labelSem = (semaine) => {
  if (!semaine) return '…';
  const d1 = new Date(semaine.date_debut + 'T00:00:00');
  const d2 = new Date(semaine.date_fin   + 'T00:00:00');
  return `S${semaine.numero_semaine} · ${d1.toLocaleDateString('fr-FR', { day:'2-digit', month:'short' })} – ${d2.toLocaleDateString('fr-FR', { day:'2-digit', month:'short', year:'numeric' })}`;
};

export default function EmployeEvolutionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { employe: authEmploye } = useAuth();
  const token = localStorage.getItem('sogas_token');

  const [offsetSemaine, setOffsetSemaine] = useState(0);
  const [semaine, setSemaine]   = useState(null);
  const [recap, setRecap]       = useState(null);
  const [employe, setEmploye]   = useState(null);
  const [loading, setLoading]   = useState(true);

  const estPropre = authEmploye?.id?.toString() === id?.toString();

  useEffect(() => {
    if (!employe && id) {
      axios.get(`${API}/api/employes/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setEmploye(r.data?.employe || r.data))
        .catch(console.error);
    }
  }, [id, token, employe]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('date', dateRef(offsetSemaine));

      const { data: sem } = await axios.get(
        `${API}/api/temps/semaine-en-cours?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSemaine(sem.semaine || null);

      if (sem.semaine?.id) {
        const { data: recapData } = await axios.get(
          `${API}/api/temps/semaine/${sem.semaine.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const allRecaps = recapData.recaps || [];
        const found = allRecaps.find(r => r.employe_id?.toString() === id?.toString());
        setRecap(found || null);
      } else {
        setRecap(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [offsetSemaine, id, token]);

  useEffect(() => { charger(); }, [charger]);

  const retourPath = estPropre ? '/accueil-employe' : `/employes/${id}`;

  const totH      = parseFloat(recap?.h_normales || 0) + parseFloat(recap?.h_hs_15 || 0) + parseFloat(recap?.h_hs_40 || 0);
  const joursDispo = recap?.jours || [];

  return (
    <Layout>
      <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', minHeight: '100vh', background: '#f7f7f7' }}>

        {/* ══ HEADER VERT COMPACT ══ */}
        <div className="sg-bandeau-clair" style={{ background: '#1B5E20', padding: '14px 18px 18px', position: 'sticky', top: 0, zIndex: 100, overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -20, right: -20, width: 100, height: 100, background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />

          {/* Ligne 1 : Logo + cloche + retour */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, position: 'relative', zIndex: 1 }}>
            <img src="/logo-sogas1.png" alt="SOGAS"
              style={{ height: 28, objectFit: 'contain', display: 'block' }}
              onError={e => { e.target.onerror = null; e.target.src = '/logo-sogas.png'; }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <NotificationBell couleurIcone="#fff" taille={16} />
              <button onClick={() => navigate(retourPath)}
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 20, padding: '4px 12px', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer' }}>
                ← Retour
              </button>
            </div>
          </div>

          {/* Ligne 2 : Titre + nom employé */}
          <div style={{ position: 'relative', zIndex: 1, marginBottom: 10 }}>
            <p style={{ margin: '0 0 2px 0', color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>
              Mon évolution{employe ? ` — ${employe.prenom} ${employe.nom}` : ''}
            </p>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.65)', fontSize: '0.65rem' }}>
              {employe?.matricule}{employe?.poste ? ` · ${employe.poste}` : ''}
            </p>
          </div>

          {/* Ligne 3 : Navigateur semaine */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 8px', width: 'fit-content', position: 'relative', zIndex: 1 }}>
            <button onClick={() => setOffsetSemaine(o => o - 1)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>‹</button>
            <span style={{ fontSize: '0.72rem', whiteSpace: 'nowrap', color: '#fff', fontWeight: 600 }}>{labelSem(semaine)}</span>
            <button onClick={() => setOffsetSemaine(o => o + 1)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}>›</button>
            {offsetSemaine !== 0 && (
              <button onClick={() => setOffsetSemaine(0)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: '0.6rem', padding: '2px 6px', borderRadius: 4 }}>
                Auj.
              </button>
            )}
          </div>
        </div>

        <div style={{ padding: '14px' }}>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: '0.82rem' }}>Chargement…</div>
          ) : !recap ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#aaa', fontSize: '0.82rem' }}>
              Aucune donnée pour cette semaine.
            </div>
          ) : (
            <>
              {/* ══ STATS 3×2 ══ */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
                {[
                  { label: 'Heures norm.', val: fmt2(recap.h_normales), color: '#1B5E20' },
                  { label: 'Heures sup.',  val: fmt2((parseFloat(recap.h_hs_15 || 0) + parseFloat(recap.h_hs_40 || 0)).toFixed(2)), color: parseFloat(recap.h_hs_15 || 0) + parseFloat(recap.h_hs_40 || 0) > 0 ? '#F57F17' : '#ccc' },
                  { label: 'Total heures', val: fmt2(totH), color: '#1565C0' },
                  { label: 'Absences',     val: fmt2(recap.h_absent_injust), color: parseFloat(recap.h_absent_injust || 0) > 0 ? '#C62828' : '#ccc' },
                  { label: 'Paniers',      val: Math.round(recap.nb_paniers || 0), color: '#558B2F' },
                  { label: 'Semaine',      val: semaine?.statut === 'cloturee' ? 'Clôturée' : 'Ouverte', color: semaine?.statut === 'cloturee' ? '#1B5E20' : '#F57F17', small: true },
                ].map(({ label, val, color, small }) => (
                  <div key={label} style={{ background: '#fff', borderRadius: 12, padding: '10px 12px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                    <p style={{ fontSize: '0.58rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 4px 0', fontWeight: 600 }}>{label}</p>
                    <p style={{ fontSize: small ? '0.75rem' : '1.2rem', fontWeight: 800, margin: 0, color }}>{val}</p>
                  </div>
                ))}
              </div>

              {/* ══ DÉTAIL JOURNALIER ══ */}
              <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                <div style={{ padding: '12px 14px', borderBottom: '1px solid #f0f0f0' }}>
                  <p style={{ margin: 0, fontSize: '0.68rem', fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Détail de la semaine</p>
                </div>

                {joursDispo.length === 0 ? (
                  <p style={{ textAlign: 'center', color: '#aaa', padding: 24, fontSize: '0.78rem' }}>Aucun pointage enregistré.</p>
                ) : (
                  <div>
                    {joursDispo.map((j, i) => {
                      const present = !!j.heure_arrivee;
                      const complet = present && !!j.heure_depart;
                      const d = new Date(j.date + 'T00:00:00');
                      const nomJour = d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'short' });
                      return (
                        <div key={j.id || i} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '10px 14px',
                          borderBottom: i < joursDispo.length - 1 ? '1px solid #f0f0f0' : 'none',
                          background: !present ? '#fafafa' : 'transparent',
                        }}>
                          {/* Indicateur présence */}
                          <div style={{
                            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                            background: !present ? '#e0e0e0' : complet ? '#1B5E20' : '#F57F17',
                          }} />

                          {/* Nom du jour */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 600, color: '#2c2c2c', textTransform: 'capitalize' }}>{nomJour}</p>
                            {j.shift_nom && <p style={{ margin: 0, fontSize: '0.62rem', color: '#888' }}>{j.shift_nom}</p>}
                          </div>

                          {/* Arrivée / Départ */}
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            {!present ? (
                              <span style={{ fontSize: '0.65rem', color: '#bbb' }}>Absent</span>
                            ) : (
                              <>
                                <p style={{ margin: 0, fontSize: '0.72rem', color: '#1B5E20', fontWeight: 600 }}>{fmtH(j.heure_arrivee)} → {fmtH(j.heure_depart)}</p>
                                {parseFloat(j.h_travaillees || 0) > 0 && (
                                  <p style={{ margin: 0, fontSize: '0.62rem', color: '#888' }}>{fmt2(j.h_travaillees)} h</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Copyright ── */}
          <p style={{ textAlign: 'center', fontSize: '0.6rem', color: '#bbb', margin: '16px 0 0 0', paddingBottom: 8 }}>
            © {new Date().getFullYear()} SOGAS — Tous droits réservés
          </p>

        </div>
      </div>
    </Layout>
  );
}
